/* Shopify storefront behavior. Decorative motion is isolated in boxzy-motion.js. */
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const mobile = matchMedia('(max-width: 1099px)');
  const dialogs = new WeakMap();
  const drawerAnimations = new WeakMap();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const animateDrawers = () => !reducedMotion.matches && document.body.dataset.boxzyMotion !== 'false';
  const feedbackStates = new WeakMap();
  let cartBusy = false;
  const PRODUCT_LIMIT = 2;
  const limitMessage = 'Maximum 2 of each product per order. Reduce the quantity in your box to continue.';
  const currentCart = () => request(root+'cart.js').then(r=>r.json());
  const productQuantity = (cart, productId, exceptKey) => cart.items.filter(line=>String(line.product_id)===String(productId) && line.key!==exceptKey).reduce((sum,line)=>sum+line.quantity,0);
  function validateCart(cart, updates = {}) {
    const totals = new Map();
    for (const line of cart.items) {
      const quantity = Object.hasOwn(updates,line.key) ? updates[line.key] : line.quantity;
      if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Enter a valid whole-number quantity.');
      const id = String(line.product_id);totals.set(id,(totals.get(id)||0)+quantity);
    }
    if ([...totals.values()].some(q=>q>PRODUCT_LIMIT)) throw new Error(limitMessage);
  }
  function syncQuantityButtons(scope = document) {
    const steppers=[...scope.querySelectorAll('[data-quantity]')];
    if(scope.matches?.('[data-quantity]'))steppers.push(scope);
    steppers.forEach(stepper=>{
      const input=stepper.querySelector('input');if(!input)return;
      const plus=stepper.querySelector('[data-quantity-step="1"]');const minus=stepper.querySelector('[data-quantity-step="-1"]');
      if(plus)plus.disabled=input.max!==''&&Number(input.value)>=Number(input.max);
      if(minus)minus.disabled=Number(input.value)<=Number(input.min||0);
    });
  }
  const filters = new Map();
  const galleries = new WeakSet();
  const accordions = new WeakSet();
  const request = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) { let error; try { error = await response.json(); } catch {} throw new Error(error?.description || 'Something went wrong. Please try again.'); }
    return response;
  };
  const status = message => {
    const dialog = document.querySelector('dialog[open]');
    let region = dialog?.querySelector('[data-dialog-status]');
    if (dialog && !region) { region = document.createElement('p'); region.dataset.dialogStatus = ''; region.setAttribute('role','status'); dialog.append(region); }
    const target = region || document.querySelector('#store-status');
    if (target) target.textContent = message;
  };
  function animateDrawer(dialog, closing, from) {
    drawerAnimations.get(dialog)?.cancel();
    dialog.dataset.drawerMotion = closing ? 'closing' : 'opening';
    const animation = dialog.animate([
      { transform: from },
      { transform: closing ? 'translateX(100%)' : 'translateX(0)' }
    ], { duration: closing ? 220 : 360, easing: closing ? 'cubic-bezier(.4,0,1,1)' : 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' });
    drawerAnimations.set(dialog, animation);
    animation.finished.then(() => {
      if (drawerAnimations.get(dialog) !== animation) return;
      drawerAnimations.delete(dialog);
      if (closing && dialog.open) dialog.close();
      animation.cancel();
      delete dialog.dataset.drawerMotion;
    }).catch(() => {});
  }
  function closeDrawer(dialog) {
    if (!dialog?.open || dialog.dataset.drawerMotion === 'closing') return;
    if (!dialog.matches('.drawer') || !animateDrawers() || !dialog.animate) {
      dialog.close();
      return;
    }
    animateDrawer(dialog, true, getComputedStyle(dialog).transform);
  }
  function open(dialog, trigger = document.activeElement) {
    if (!dialog || (dialog.open && dialog.dataset.drawerMotion !== 'closing')) return;
    const from = dialog.open ? getComputedStyle(dialog).transform : 'translateX(100%)';
    dialogs.set(dialog, trigger);
    if (!dialog.open) dialog.showModal();
    trigger?.setAttribute('aria-expanded','true');
    document.body.classList.add('dialog-open');
    if (dialog.matches('.drawer') && animateDrawers() && dialog.animate) animateDrawer(dialog, false, from);
  }
  document.addEventListener('cancel', event => {
    if (!event.target.matches?.('dialog.drawer')) return;
    event.preventDefault();
    closeDrawer(event.target);
  }, true);
  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    document.querySelectorAll('dialog[data-drawer-motion]').forEach(dialog => {
      const closing = dialog.dataset.drawerMotion === 'closing';
      drawerAnimations.get(dialog)?.cancel();
      drawerAnimations.delete(dialog);
      delete dialog.dataset.drawerMotion;
      if (closing) dialog.close();
    });
  });
  document.addEventListener('close', event => {
    if (event.target.tagName !== 'DIALOG' || event.target.open) return;
    drawerAnimations.get(event.target)?.cancel();
    drawerAnimations.delete(event.target);
    delete event.target.dataset.drawerMotion;
    const trigger = dialogs.get(event.target);
    trigger?.setAttribute('aria-expanded','false');
    if (trigger?.isConnected) trigger.focus();
    document.body.classList.toggle('dialog-open', Boolean(document.querySelector('dialog[open]')));
  }, true);
  async function refreshCart() {
    const [html,cart] = await Promise.all([
      request(root+'?section_id=cart-content').then(r=>r.text()),
      request(root+'cart.js').then(r=>r.json())
    ]);
    const body = document.querySelector('[data-cart-body]');
    body.innerHTML = new DOMParser().parseFromString(html,'text/html').querySelector('.shopify-section')?.innerHTML || html;
    document.querySelectorAll('[data-cart-count]').forEach(el=>{el.textContent=cart.item_count;});
    syncQuantityButtons(body);
    document.dispatchEvent(new CustomEvent('boxzy:content-updated',{detail:{scope:body}}));
  }
  async function changeCart(id,quantity) {
    const dialog = document.querySelector('#cart-dialog');
    if (cartBusy || dialog.dataset.busy) {status('Updating your box. Please try again in a moment.');return;}
    cartBusy = true;
    dialog.dataset.busy='true'; dialog.setAttribute('aria-busy','true');
    try {
      const cart=await currentCart();const line=cart.items.find(item=>item.key===id);
      if(!line)throw new Error('Your box changed. Reopen it and try again.');
      if(!Number.isInteger(quantity)||quantity<0)throw new Error('Enter a valid whole-number quantity.');
      if(quantity>=line.quantity && productQuantity(cart,line.product_id,id)+quantity>PRODUCT_LIMIT)throw new Error(limitMessage);
      await request(root+'cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id,quantity})});
      await refreshCart();
      const input=[...dialog.querySelectorAll('[data-line-key]')].find(el=>el.dataset.lineKey===id);
      (input || dialog.querySelector('[data-close]')).focus();
      status('Your box is updated.');
    } catch(error) {status(error.message);} finally {cartBusy=false;delete dialog.dataset.busy;dialog.removeAttribute('aria-busy');syncQuantityButtons();}
  }
  function clearFeedback(button) {
    const state=feedbackStates.get(button);
    if (state) {clearTimeout(state.timer);button.innerHTML=state.original;feedbackStates.delete(button);}
    return button.innerHTML;
  }
  function addedFeedback(button,original) {
    button.textContent='ADDED ✓';
    const timer=setTimeout(()=>{if(button.isConnected)button.innerHTML=original;feedbackStates.delete(button);},1000);
    feedbackStates.set(button,{original,timer});
  }
  document.addEventListener('submit',async event=>{
    const form=event.target;
    if(form.matches('[data-cart-form]')) {
      event.preventDefault();if(cartBusy){status('Updating your box. Please try again in a moment.');return;}
      cartBusy=true;form.setAttribute('aria-busy','true');
      try {
        const cart=await currentCart();const updates={};
        for(const input of form.querySelectorAll('[data-line-key]')) {
          if(!cart.items.some(line=>line.key===input.dataset.lineKey))throw new Error('Your box changed. Refresh the page before continuing.');
          updates[input.dataset.lineKey]=Number(input.value);
        }
        validateCart(cart,updates);
        await request(root+'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates})});
        if(event.submitter?.name==='checkout'){validateCart(await currentCart());location.assign(root+'checkout');}
        else if(form.closest('#cart-dialog')){await refreshCart();document.querySelector('#cart-dialog [data-close]').focus();status('Your box is updated.');}
        else location.reload();
      } catch(error){status(error.message);}
      finally{cartBusy=false;form.removeAttribute('aria-busy');}return;
    }
    if(!form.matches('[data-product-form]'))return;
    if(cartBusy){event.preventDefault();status('Updating your box. Please try again in a moment.');return;}
    event.preventDefault();if(form.dataset.busy)return;
    const button=event.submitter || form.querySelector('button:not([type="button"])');
    if(!button || button.disabled)return;
    cartBusy=true;
    const original=clearFeedback(button);
    const data=new FormData(form);
    button.textContent='Adding…';button.disabled=true;form.dataset.busy='true';form.setAttribute('aria-busy','true');
    let added=false;
    try {
      const cart=await currentCart();const quantity=Number(data.get('quantity')||1);
      if(!form.dataset.productId)throw new Error('Please refresh the page before adding this product.');
      if(!Number.isInteger(quantity)||quantity<1)throw new Error('Enter a valid whole-number quantity.');
      if(productQuantity(cart,form.dataset.productId)+quantity>PRODUCT_LIMIT)throw new Error(limitMessage);
      await request(root+'cart/add.js',{method:'POST',body:data});added=true;addedFeedback(button,original);
      if(document.body.dataset.cartEnabled!=='true'){location.assign(root+'cart');return;}
      await refreshCart();open(document.querySelector('#cart-dialog'),button);status('Added to your box.');
      document.dispatchEvent(new CustomEvent('boxzy:cart-added'));
    } catch(error) {if(added)location.assign(root+'cart');else{button.innerHTML=original;status(error.message);}}
    finally {cartBusy=false;button.disabled=false;delete form.dataset.busy;form.removeAttribute('aria-busy');}
  });
  function toggleAnswer(button,expanded) {
    const panel=document.getElementById(button.getAttribute('aria-controls'));
    if(!panel)return;
    button.setAttribute('aria-expanded',String(expanded));panel.hidden=!expanded;
  }
  function showMedia(shell,id) {
    const panels=[...shell.querySelectorAll('[data-media-panel]')];
    if(!panels.some(panel=>panel.id===id))return;
    panels.forEach(panel=>{panel.hidden=panel.id!==id;if(panel.hidden)panel.querySelectorAll('video').forEach(video=>video.pause());});
    shell.querySelectorAll('[data-media-target]').forEach(link=>{
      if(link.dataset.mediaTarget===id)link.setAttribute('aria-current','true');else link.removeAttribute('aria-current');
    });
  }
  document.addEventListener('click',async event=>{
    const trigger=event.target.closest('[data-open]');
    if(trigger) {
      if(trigger.dataset.open==='cart-dialog' && document.body.dataset.cartEnabled!=='true')return;
      event.preventDefault();open(document.getElementById(trigger.dataset.open),trigger);
      if(trigger.dataset.open==='cart-dialog') {
        try {document.querySelector('[data-cart-body]').innerHTML='<div class="bz-cart-loading" role="status">Loading your box…</div>';await refreshCart();}
        catch(error){status(error.message);const link=document.createElement('a');link.href=root+'cart';link.textContent='Open cart';document.querySelector('[data-cart-body]').replaceChildren(link);}
      }
    }
    if(event.target.closest('[data-close]')){event.preventDefault();closeDrawer(event.target.closest('dialog'));}
    const remove=event.target.closest('[data-remove]');
    if(remove?.closest('#cart-dialog')){event.preventDefault();await changeCart(remove.dataset.remove,0);}
    const step=event.target.closest('[data-quantity-step]');
    if(step) {
      const input=step.closest('[data-quantity]').querySelector('input');
      if(!input || step.closest('[aria-busy="true"]'))return;
      if(!input.value || !Number.isFinite(input.valueAsNumber))input.value=input.min || '0';
      Number(step.dataset.quantityStep)>0?input.stepUp():input.stepDown();syncQuantityButtons(step.closest('[data-quantity]'));input.dispatchEvent(new Event('change',{bubbles:true}));
    }
    const answer=event.target.closest('[data-accordion-toggle]');
    if(answer) {
      const group=answer.closest('[data-accordions]');const expand=answer.getAttribute('aria-expanded')!=='true';
      if(group.dataset.multiple!=='true')group.querySelectorAll('[data-accordion-toggle]').forEach(button=>toggleAnswer(button,false));
      toggleAnswer(answer,expand);
    }
    const media=event.target.closest('[data-media-target]');
    if(media){event.preventDefault();showMedia(media.closest('[data-product-gallery]'),media.dataset.mediaTarget);}
    const motion=event.target.closest('[data-motion-toggle]');
    if(motion){const paused=motion.getAttribute('aria-pressed')!=='true';motion.setAttribute('aria-pressed',String(paused));motion.textContent=paused?'Play announcements':'Pause announcements';motion.closest('.announcement').classList.toggle('is-paused',paused);}
  });
  document.addEventListener('input',event=>{if(event.target.matches('[data-quantity] input'))syncQuantityButtons(event.target.closest('[data-quantity]'));});
  let searchTimer,searchRequest;
  document.addEventListener('input',event=>{
    if(!event.target.matches('[data-predictive]'))return;
    clearTimeout(searchTimer);searchRequest?.abort();
    const input=event.target;const term=input.value.trim();const results=document.querySelector('#search-results');const suggestions=document.querySelector('[data-search-suggestions]');
    results.replaceChildren();if(suggestions)suggestions.hidden=term.length>=2;
    if(term.length<2)return;
    searchTimer=setTimeout(async()=>{
      searchRequest=new AbortController();results.setAttribute('aria-busy','true');
      try {
        const html=await request(root+'search/suggest?section_id=predictive-search&resources[type]=product,collection,page&q='+encodeURIComponent(term),{signal:searchRequest.signal}).then(r=>r.text());
        if(input.value.trim()===term)results.innerHTML=new DOMParser().parseFromString(html,'text/html').querySelector('.shopify-section')?.innerHTML || '';
      }catch(error){if(error.name!=='AbortError')results.textContent='Use Search to see all results.';}finally{results.removeAttribute('aria-busy');}
    },250);
  });
  function provinces(country) {
    const select=country.closest('form').querySelector('[data-province]');
    if(!select)return;
    const values=JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]');
    select.replaceChildren(...values.map(([value,label])=>new Option(label,value,false,value===select.dataset.default)));select.closest('label').hidden=!values.length;
  }
  document.addEventListener('change',event=>{
    if(event.target.matches('[data-variant-select]')){const url=new URL(location.href);url.searchParams.set('variant',event.target.value);location.assign(url);}
    if(event.target.matches('[data-country]'))provinces(event.target);
    if(event.target.matches('[data-line-key]') && event.target.closest('#cart-dialog') && event.target.checkValidity())changeCart(event.target.dataset.lineKey,event.target.valueAsNumber);
  });
  function placeFilters() {
    filters.forEach(({home,panel,destination,dialog},section)=>{
      if(!section.isConnected){filters.delete(section);return;}
      if(!mobile.matches && dialog.open)dialog.close();
      (mobile.matches?destination:home).append(panel);panel.open=true;
    });
  }
  let headerFrame=0;
  function updateHeaders() {
    headerFrame=0;
    document.querySelectorAll('[data-header]').forEach(header=>{
      header.classList.toggle('is-compact',scrollY>60);
      const hero=document.querySelector('#MainContent>.shopify-section:first-child .hero') || document.querySelector('main>.hero');
      header.classList.toggle('is-over-hero',header.dataset.transparent==='true' && Boolean(hero) && !hero?.classList.contains('hero--cream') && scrollY<60);
    });
  }
  document.addEventListener('scroll',()=>{if(!headerFrame)headerFrame=requestAnimationFrame(updateHeaders);},{passive:true});
  mobile.addEventListener('change',placeFilters);
  function init(scope=document) {
    scope.querySelectorAll('[data-country]').forEach(el=>{if(el.dataset.default)el.value=el.dataset.default;provinces(el);});
    scope.querySelectorAll('[data-accordions]').forEach(group=>{if(accordions.has(group))return;accordions.add(group);group.querySelectorAll('[data-accordion-toggle]').forEach(button=>toggleAnswer(button,false));});
    scope.querySelectorAll('[data-product-gallery]').forEach(shell=>{if(galleries.has(shell))return;galleries.add(shell);const initial=shell.querySelector('[data-initial-media]') || shell.querySelector('[data-media-panel]');if(initial)showMedia(shell,initial.id);});
    scope.querySelectorAll('[data-filter-home]').forEach(home=>{
      const section=home.closest('.shopify-section') || home.closest('section');if(filters.has(section))return;
      const panel=home.querySelector('[data-filter-panel]');const dialog=section.querySelector('[data-filter-dialog]');
      if(panel && dialog){filters.set(section,{home,panel,dialog,destination:dialog.querySelector('[data-filter-destination]')});section.classList.add('filter-ready');}
    });syncQuantityButtons(scope);placeFilters();updateHeaders();
    scope.querySelectorAll('[data-recommendations]').forEach(async el=>{
      if(el.dataset.loaded)return;el.dataset.loaded='true';
      try {const html=await request(el.dataset.recommendations).then(r=>r.text());if(!el.isConnected)return;const content=new DOMParser().parseFromString(html,'text/html').querySelector('[data-recommendations]');if(content){el.innerHTML=content.innerHTML;document.dispatchEvent(new CustomEvent('boxzy:content-updated',{detail:{scope:el}}));}}
      catch{el.hidden=true;}
    });
  }
  document.addEventListener('keydown',event=>{
    if(event.key==='Escape')document.querySelectorAll('.mega-menu[open]').forEach(menu=>{menu.open=false;menu.querySelector('summary').focus();});
    if(event.target.matches('[data-media-target]') && ['ArrowLeft','ArrowRight'].includes(event.key)){
      event.preventDefault();const links=[...event.target.closest('.thumbnails').querySelectorAll('[data-media-target]')];const i=(links.indexOf(event.target)+(event.key==='ArrowRight'?1:-1)+links.length)%links.length;links[i].click();links[i].focus();
    }
  });
  document.addEventListener('toggle',event=>{if(event.target.matches?.('.mega-menu[open]'))document.querySelectorAll('.mega-menu[open]').forEach(menu=>{if(menu!==event.target)menu.open=false;});},true);
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    event.target.querySelectorAll('dialog[open]').forEach(dialog=>dialog.close());
    filters.delete(event.target);document.body.classList.toggle('dialog-open',Boolean(document.querySelector('dialog[open]')));
  });
  document.body.dataset.cartEnhanced = 'true';
  init();
})();
